function retroLensFocus(selector, {
  duration = 6,
  ease = "power2.inOut"
} = {}) {
  const img = document.querySelector(selector);
  if (!img || !img.complete) {
    img?.addEventListener("load", () => retroLensFocus(selector, { duration, ease }), { once: true });
    return;
  }

  const container = img.parentElement;
  if (getComputedStyle(container).position === "static") {
    container.style.position = "relative";
  }

  const rect = img.getBoundingClientRect();
  const dispW = Math.round(rect.width);
  const dispH = Math.round(rect.height);
  const natW = img.naturalWidth;
  const natH = img.naturalHeight;

  const style = getComputedStyle(img);
  const fit = style.objectFit || "fill";
  const [xRaw, yRaw] = (style.objectPosition || "50% 50%").split(" ");
  const xVal = parseFloat(xRaw) / 100 || 0.5;
  const yVal = parseFloat(yRaw) / 100 || 0.5;

  let uMin = 0, vMin = 0, uMax = 1, vMax = 1;
  if (fit === "cover" || fit === "contain") {
    const scale = fit === "cover"
      ? Math.max(dispW / natW, dispH / natH)
      : Math.min(dispW / natW, dispH / natH);
    const drawW = natW * scale;
    const drawH = natH * scale;
    const extraW = Math.max(0, drawW - dispW);
    const extraH = Math.max(0, drawH - dispH);

    uMin = (extraW / drawW) * xVal;
    uMax = 1 - (extraW / drawW) * (1 - xVal);
    vMin = (extraH / drawH) * (1 - yVal);
    vMax = 1 - (extraH / drawH) * yVal;
  }

  const canvas = document.createElement("canvas");
  canvas.width = dispW;
  canvas.height = dispH;
  Object.assign(canvas.style, {
    position: "absolute",
    top: "0",
    left: "0",
    width: `${dispW}px`,
    height: `${dispH}px`,
    pointerEvents: "none",
    zIndex: "10"
  });
  img.insertAdjacentElement("afterend", canvas);

  const gl = canvas.getContext("webgl");
  if (!gl) {
    img.style.opacity = "1";
    canvas.remove();
    return;
  }

  gl.viewport(0, 0, canvas.width, canvas.height);

  const vsSource = `
    attribute vec2 aPos;
    varying vec2 vUV;
    void main() {
      vUV = aPos * 0.5 + 0.5;
      gl_Position = vec4(aPos, 0, 1);
    }
  `;

  const fsSource = `
    precision mediump float;
    uniform sampler2D uImage;
    uniform float uBlur;
    uniform float uAberration;
    uniform vec4 uCrop;
    varying vec2 vUV;

    vec4 getBlurredColor(vec2 uv, float blur) {
      vec4 color = vec4(0.0);
      float total = 0.0;
      for (float x = -4.0; x <= 4.0; x++) {
        for (float y = -4.0; y <= 4.0; y++) {
          float weight = 1.0 - abs(x + y) / 8.0;
          vec2 offset = vec2(x, y) * blur / 100.0;
          color += texture2D(uImage, uv + offset) * weight;
          total += weight;
        }
      }
      return color / total;
    }

    void main() {
      vec2 uv = mix(uCrop.xy, uCrop.zw, vUV);
      vec2 offset = (uv - 0.5) * uAberration;

      vec4 r = getBlurredColor(uv + offset, uBlur);
      vec4 g = getBlurredColor(uv,         uBlur);
      vec4 b = getBlurredColor(uv - offset, uBlur);

      gl_FragColor = vec4(r.r, g.g, b.b, (r.a + g.a + b.a) / 3.0);
    }
  `;

  function createShader(src, type) {
    const s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
      console.error(gl.getShaderInfoLog(s));
    }
    return s;
  }

  const vert = createShader(vsSource, gl.VERTEX_SHADER);
  const frag = createShader(fsSource, gl.FRAGMENT_SHADER);
  const program = gl.createProgram();
  gl.attachShader(program, vert);
  gl.attachShader(program, frag);
  gl.linkProgram(program);
  gl.useProgram(program);

  const posLoc = gl.getAttribLocation(program, "aPos");
  const posBuf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, posBuf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
    -1, -1,  1, -1,  1, 1,
    -1, -1,  1,  1, -1, 1
  ]), gl.STATIC_DRAW);
  gl.enableVertexAttribArray(posLoc);
  gl.vertexAttribPointer(posLoc, 2, gl.FLOAT, false, 0, 0);

  const tex = gl.createTexture();
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.uniform1i(gl.getUniformLocation(program, "uImage"), 0);

  const uBlur = gl.getUniformLocation(program, "uBlur");
  const uAberration = gl.getUniformLocation(program, "uAberration");
  const uCrop = gl.getUniformLocation(program, "uCrop");

  gl.uniform4f(uCrop, uMin, vMin, uMax, vMax);

  const state = { blur: 4.0, ab: 0.04 };

  function render() {
    gl.uniform1f(uBlur, state.blur);
    gl.uniform1f(uAberration, state.ab);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
  }

  img.style.opacity = "0";
  render();

  gsap.to(state, {
    blur: 0.0,
    ab: 0.0,
    duration,
    ease,
    onUpdate: render,
  });

  gsap.to(img.style, {
    opacity: "1",
    duration,
    ease: "linear",
    onComplete() {
      requestAnimationFrame(() => canvas.remove());
    }
  });
}

// Usage
retroLensFocus("#owl_img", {
  duration: 4,
  ease: "power4.inOut"
});
